import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + 24 * 60 * 60 * 1000;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + 24 * 60 * 60 * 1000;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(24 * 60 * 60 * 1000);
  useEffect(() => {
    const t = getInitialTarget();
    setTarget(t);
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + 24 * 60 * 60 * 1000;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(24 * 60 * 60 * 1000);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = 24 * 60 * 60 * 1000 - remaining;
  const progress = Math.min(90, 65 + elapsed / (24 * 60 * 60 * 1000) * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-gradient-to-b from-[#2a0808] to-[#1a0404] border border-red-500/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(239,68,68,0.15)]" data-id="element-42">
      <div className="text-center" data-id="element-43">
        <div className="flex items-center justify-center gap-2 text-red-400 font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-44">
          <AlarmClock size={20} className="animate-pulse" data-id="element-45" />
          <span data-id="element-46">The launch price ends in:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-47">
          <TimeBox value={pad(totalHours)} label="hrs" data-id="element-48" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-49">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-50" />
          <span className="text-3xl md:text-5xl font-bold text-red-400" data-id="element-51">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-52" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-gray-200 mb-8" data-id="element-53">
          After this the price goes up and it will not come back down.
        </p>

        <div className="space-y-2" data-id="element-54">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-55">
            <span className="text-red-300" data-id="element-56">Launch price spots remaining</span>
            <span className="text-red-400" data-id="element-57">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-red-950/60 rounded-full overflow-hidden border border-red-500/30" data-id="element-58">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.6)]" data-id="element-59" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-red-300 font-medium" data-id="element-60">
            {Math.round(progress)}% of spots taken
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-black border-2 border-red-500/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-[inset_0_0_15px_rgba(239,68,68,0.1)]" data-id="element-61">
      <div className="flex items-baseline justify-center gap-1" data-id="element-62">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-63">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-red-300" data-id="element-64">
          {label}
        </span>
      </div>
    </div>;
}